import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigation } from "../../../hooks";
import { useAuth } from "../../../hooks/useAuth";
import { deleteAccount } from "../../../services";
import { BottomActionButton } from "../../ui";
import Card from "../../ui/Card";
import Icon from "../../ui/Icon";

const AccountDeletionPage: React.FC = () => {
  const { goBack, navigateToHome } = useNavigation();
  const { logout } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const handleAccountDeletion = async () => {
    if (!confirmChecked) {
      toast.error("회원 탈퇴에 대한 확인 체크박스를 선택해주세요.");
      return;
    }

    if (
      !window.confirm(
        "정말로 회원 탈퇴를 하시겠습니까? 이 작업은 되돌릴 수 없습니다."
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteAccount();

      toast.success("회원 탈퇴가 완료되었습니다.");
      logout();
      navigateToHome();
    } catch (error) {
      console.error("회원 탈퇴 오류:", error);
      toast.error("회원 탈퇴 중 오류가 발생했습니다.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="h-screen-dvh bg-white flex flex-col overflow-hidden">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center flex-shrink-0">
        <button
          onClick={goBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevron-left" className="text-gray-600" />
        </button>      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 flex flex-col overflow-hidden min-h-0">
        {/* 스크롤 가능한 콘텐츠 영역 */}
        <div className="flex-1 overflow-y-auto px-4 pt-6 pb-4 md:pb-6">
          <div className="max-w-md mx-auto space-y-6 pb-20 md:pb-4">
            {/* 경고 아이콘과 제목 */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                정말로 탈퇴하시겠습니까?
              </h2>
              <p className="text-gray-600 text-sm">
                계정 삭제는 되돌릴 수 없는 작업입니다.
              </p>
            </div>

            {/* 주의사항 */}
            <Card className="p-5 border-red-200 bg-red-50 shadow-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon name="x" className="text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 mb-3">
                    회원 탈퇴 시 주의사항
                  </h3>
                  <ul className="text-sm text-red-700 space-y-2 leading-relaxed">
                    <li>• 계정과 관련된 모든 데이터가 영구적으로 삭제됩니다</li>
                    <li>
                      • 채팅 기록, 건강 데이터, 동의 정보 등이 모두 삭제됩니다
                    </li>
                    <li>• 삭제된 데이터는 복구할 수 없습니다</li>
                    <li>
                      • 동일한 정보로 재가입 시에도 기존 데이터는 복원되지
                      않습니다
                    </li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* 확인 체크박스 */}
            <Card className="p-5 shadow-sm">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="mt-1 w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                />
                <div className="text-gray-700">
                  <p className="font-medium leading-relaxed">
                    위 내용을 모두 확인했으며, 회원 탈퇴에 동의합니다.
                  </p>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                    탈퇴 후에는 모든 데이터가 삭제되며 복구할 수 없음을
                    이해합니다.
                  </p>
                </div>
              </label>
            </Card>
          </div>
        </div>
      </main>

      <BottomActionButton
        onClick={handleAccountDeletion}
        isLoading={isDeleting}
        disabled={!confirmChecked}
        loadingText="탈퇴 처리 중..."
      >
        회원 탈퇴
      </BottomActionButton>
    </div>
  );
};

export default AccountDeletionPage;
